/**
 * Historico de versoes de um modelo.
 *
 * Decisoes nao obvias:
 *  1. A versao guarda so o CONTEUDO (texto, botoes, rodape, assunto, roteiro,
 *     mapa de variaveis) e o status daquele momento. Nome, canal e empresa
 *     ficam no modelo: restaurar uma versao antiga nao pode mover o modelo de
 *     empresa nem renomea-lo por baixo de uma regua ligada.
 *  2. Restaurar CRIA uma versao nova ("Restaurado da v2"), nunca apaga as do
 *     meio. Historico que some quando alguem desfaz e historico que nao serve
 *     para auditoria.
 *  3. Na API oficial, qualquer mudanca de conteudo -- inclusive restaurar --
 *     volta para a analise da Meta (`pendente`). Um modelo aprovado nao pode
 *     trocar de texto e continuar "aprovado".
 *  4. Na previa o historico vive no proprio objeto do modelo, dentro da
 *     sobreposicao local da pagina; some no F5, como toda interacao da previa.
 */

import { formatDate } from '../../lib/format';
import type { MessageTemplate, TemplateContent, TemplateVersion } from './types';
import { canalPassaPelaMeta, extrairVariaveis } from './vocabulario';

export const AUTOR_LOCAL = 'Você (prévia)';

export function conteudoDe(t: TemplateContent): TemplateContent {
  const base: TemplateContent = {
    body: t.body,
    footer: t.footer,
    buttons: t.buttons,
    voice: t.voice,
    variableMap: t.variableMap,
  };
  return {
    ...base,
    ...(t.subject === undefined ? {} : { subject: t.subject }),
    ...(t.preheader === undefined ? {} : { preheader: t.preheader }),
  };
}

function mesmoTexto(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '') === (b ?? '');
}

/** Rotulos do que mudou entre duas formas do modelo, na ordem em que aparecem no editor. */
export function resumirMudancas(antes: MessageTemplate, depois: MessageTemplate): readonly string[] {
  const mudou: string[] = [];
  if (antes.name !== depois.name) {
    mudou.push('nome');
  }
  if (antes.channel !== depois.channel) {
    mudou.push('canal');
  }
  if (antes.category !== depois.category) {
    mudou.push('categoria');
  }
  if (antes.audience !== depois.audience || antes.touch !== depois.touch) {
    mudou.push('público');
  }
  if (antes.companyId !== depois.companyId) {
    mudou.push('empresa');
  }
  if (!mesmoTexto(antes.subject, depois.subject)) {
    mudou.push('assunto');
  }
  if (!mesmoTexto(antes.preheader, depois.preheader)) {
    mudou.push('pré-cabeçalho');
  }
  if (depois.channel === 'voice') {
    if (JSON.stringify(antes.voice) !== JSON.stringify(depois.voice)) {
      mudou.push('roteiro');
    }
  } else if (!mesmoTexto(antes.body, depois.body)) {
    mudou.push('texto');
  }
  if (!mesmoTexto(antes.footer, depois.footer)) {
    mudou.push('rodapé');
  }
  if (JSON.stringify(antes.buttons) !== JSON.stringify(depois.buttons)) {
    mudou.push('botões');
  }
  if (JSON.stringify(antes.variableMap) !== JSON.stringify(depois.variableMap)) {
    mudou.push('variáveis');
  }
  return mudou;
}

export function frasearMudancas(mudancas: readonly string[]): string {
  if (mudancas.length === 0) {
    return 'Salvo sem mudanças';
  }
  const [primeira, ...resto] = mudancas;
  const inicial = `${(primeira ?? '').charAt(0).toUpperCase()}${(primeira ?? '').slice(1)}`;
  if (resto.length === 0) {
    return inicial;
  }
  const ultima = resto[resto.length - 1] ?? '';
  const meio = resto.slice(0, -1);
  return `${[inicial, ...meio].join(', ')} e ${ultima}`;
}

export function proximaVersao(t: Pick<MessageTemplate, 'versions'>): number {
  const ultima = t.versions[t.versions.length - 1];
  return ultima === undefined ? 1 : ultima.version + 1;
}

export function versaoDe(t: MessageTemplate, numero: number, quando: string, resumo: string, autor: string = AUTOR_LOCAL): TemplateVersion {
  return {
    version: numero,
    savedAt: quando,
    savedBy: autor,
    summary: resumo,
    status: t.status,
    statusNote: t.statusNote,
    content: conteudoDe(t),
  };
}

/** Devolve o modelo com o conteudo da versao escolhida e uma versao nova no fim. */
export function restaurarVersao(atual: MessageTemplate, versao: TemplateVersion, quando: string): MessageTemplate {
  const meta = canalPassaPelaMeta(atual.channel);
  const conteudo = versao.content;
  const restaurado: MessageTemplate = {
    ...atual,
    body: conteudo.body,
    footer: conteudo.footer,
    buttons: conteudo.buttons,
    voice: conteudo.voice,
    variableMap: conteudo.variableMap,
    subject: conteudo.subject,
    preheader: conteudo.preheader,
    variables: extrairVariaveis(conteudo.body, conteudo.subject, conteudo.preheader, conteudo.footer, conteudo.voice?.objection, conteudo.voice?.closing, ...conteudo.buttons.map((b) => b.url)),
    status: meta ? 'pendente' : 'texto_livre',
    statusNote: meta
      ? `Restaurado da v${versao.version} em ${formatDate(quando)} (prévia): toda mudança de texto volta para a análise da Meta.`
      : `Restaurado da v${versao.version} em ${formatDate(quando)} (prévia).`,
    updatedAt: quando,
  };
  return { ...restaurado, versions: [...atual.versions, versaoDe(restaurado, proximaVersao(atual), quando, `Restaurado da v${versao.version}`)] };
}
